import { useEffect, useState } from "react";
import { MessagesSquare, Search, Filter, Loader2, RefreshCw, Trash2, Printer, FileSpreadsheet, MessageCircle, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { STAGES, STAGE_KEYS, GRADES, buildWaUrl, SCHOOL_LOGO, SCHOOL_NAME } from "../lib/constants";
import { listWaMessages, waStats, updateWaStatus, deleteWaMessage } from "../lib/api";
import { exportToExcel } from "../lib/excel";

const STATUS_LABELS = { sent: "تم الإرسال", pending: "بانتظار", failed: "فشل" };
const STATUS_STYLE = {
  sent: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  failed: "bg-rose-100 text-rose-700",
};

export default function WhatsAppLog() {
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("all");
  const [grade, setGrade] = useState("all");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (stage !== "all") params.stage = stage;
      if (grade !== "all") params.grade = grade;
      if (status !== "all") params.status = status;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const [list, st] = await Promise.all([listWaMessages(params), waStats()]);
      setRows(list);
      setStats(st);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التحميل");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onResend = async (m) => {
    const url = buildWaUrl(m.guardian_phone, m.message);
    if (!url) { toast.error("رقم غير صالح"); return; }
    window.open(url, "_blank");
    try {
      await updateWaStatus(m.id, "sent");
      toast.success("تم فتح واتساب");
      load();
    } catch {
      toast.error("تعذر تحديث الحالة");
    }
  };

  const onDelete = async () => {
    try {
      await deleteWaMessage(deleting.id);
      toast.success("تم الحذف");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحذف");
    }
  };

  const handleExcel = () => {
    if (!rows.length) { toast.info("لا توجد بيانات"); return; }
    exportToExcel(`سجل_رسائل_واتساب_${new Date().toISOString().slice(0, 10)}.xlsx`, [{
      name: "سجل الرسائل",
      columns: [
        { key: "student_name", header: "اسم الطالب" },
        { key: "guardian_name", header: "ولي الأمر" },
        { key: "guardian_phone", header: "رقم الجوال" },
        { key: "stage_label", header: "المرحلة" },
        { key: "grade", header: "الصف" },
        { key: "section", header: "الفصل" },
        { key: "absence_date", header: "تاريخ الغياب" },
        { key: "created_at", header: "وقت الإرسال" },
        { key: "status_label", header: "الحالة" },
        { key: "sender_name", header: "أرسل بواسطة" },
      ],
      rows: rows.map(r => ({ ...r, stage_label: STAGES[r.stage] || r.stage, status_label: STATUS_LABELS[r.status] || r.status })),
    }]);
    toast.success("تم تنزيل ملف Excel");
  };

  const handlePrint = () => {
    const today = new Date().toLocaleDateString("ar-SA-u-nu-latn");
    const w = window.open("", "_blank", "width=1100,height=800");
    if (!w) return;
    const rowsHtml = rows.length === 0
      ? `<tr><td colspan="9" style="text-align:center;color:#64748B;padding:18px">لا توجد بيانات</td></tr>`
      : rows.map((r, i) => `<tr><td style="text-align:center">${i + 1}</td><td>${r.student_name}</td><td>${r.guardian_name || "—"}</td><td dir="ltr">${r.guardian_phone}</td><td>${STAGES[r.stage] || ""}</td><td>${r.grade}</td><td>${r.section}</td><td dir="ltr" style="font-variant-numeric:tabular-nums">${r.absence_date}</td><td><span style="padding:2px 8px;border-radius:6px;font-size:10px;${r.status === 'sent' ? 'background:#D1FAE5;color:#047857' : r.status === 'failed' ? 'background:#FEE2E2;color:#BE123C' : 'background:#FEF3C7;color:#B45309'}">${STATUS_LABELS[r.status]}</span></td></tr>`).join("");
    w.document.write(`<!doctype html><html lang="ar" dir="rtl"><head><meta charset="utf-8"><title>سجل الرسائل</title>
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@500;700;800&family=Tajawal:wght@400;500;700&display=swap" rel="stylesheet">
      <style>body{font-family:'Tajawal','Cairo',sans-serif;padding:22px 28px;color:#0F172A}.head{display:flex;gap:14px;align-items:center;border-bottom:2px solid #0F766E;padding-bottom:12px}.head img{width:68px;height:68px;object-fit:contain}h1{font-family:'Cairo',sans-serif;color:#0F766E;margin:8px 0}table{width:100%;border-collapse:collapse;font-size:11px;margin-top:8px}thead th{background:#0F766E;color:#fff;padding:8px 6px;text-align:right;font-family:'Cairo',sans-serif}tbody td{padding:6px;border-bottom:1px solid #E2E8F0;text-align:right}tbody tr:nth-child(even) td{background:#F8FAFC}.credits{margin-top:14px;display:flex;flex-direction:column;align-items:center;gap:4px;color:#0F766E;font-size:11px;font-family:'Cairo',sans-serif;border-top:1px solid #E2E8F0;padding-top:8px}@media print{body{padding:12mm}}</style>
      </head><body>
      <div class="head"><img src="${SCHOOL_LOGO}"/><div style="font-family:'Cairo',sans-serif;font-size:19px;font-weight:800;color:#115E59">${SCHOOL_NAME}<div style="color:#475569;font-size:12px;font-weight:400">نظام إدارة الطلاب — سجل رسائل واتساب</div></div></div>
      <h1>سجل رسائل واتساب — تاريخ الطباعة ${today} (${rows.length} رسالة)</h1>
      <table><thead><tr><th style="width:30px">#</th><th>الطالب</th><th>ولي الأمر</th><th>الجوال</th><th>المرحلة</th><th>الصف</th><th>الفصل</th><th>تاريخ الغياب</th><th>الحالة</th></tr></thead><tbody>${rowsHtml}</tbody></table>
      <div class="credits"><div>إدارة المدرسة: <strong>الأستاذ محمد عبدالله الصبحي</strong></div><div>برمجة وتصميم: <strong>الأستاذ محمد كامل</strong> • <span dir="ltr">0590939015</span></div></div>
      <script>window.onload=()=>setTimeout(()=>window.print(),400)</script></body></html>`);
    w.document.close();
  };

  const pieData = stats ? [
    { name: "تم الإرسال", value: stats.sent, color: "#10B981" },
    { name: "بانتظار", value: stats.pending, color: "#F59E0B" },
    { name: "فشل", value: stats.failed, color: "#EF4444" },
  ] : [];

  return (
    <div>
      <PageHeader title="سجل رسائل واتساب" subtitle="جميع الرسائل المُسجَّلة + إحصائيات وتصدير" icon={MessagesSquare} />

      {/* Stats */}
      {stats && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <Card className="p-4 border-slate-200" data-testid="wa-stat-total">
              <div className="flex items-center gap-2 text-xs text-slate-500"><MessageCircle size={13} /> إجمالي الرسائل</div>
              <div className="text-3xl font-extrabold text-slate-800 tabular-nums">{stats.total}</div>
            </Card>
            <Card className="p-4 border-emerald-200 bg-emerald-50/40">
              <div className="flex items-center gap-2 text-xs text-slate-500"><CheckCircle2 size={13} /> ناجحة</div>
              <div className="text-3xl font-extrabold text-emerald-700 tabular-nums">{stats.sent}</div>
            </Card>
            <Card className="p-4 border-amber-200 bg-amber-50/40">
              <div className="flex items-center gap-2 text-xs text-slate-500"><Clock size={13} /> بانتظار</div>
              <div className="text-3xl font-extrabold text-amber-700 tabular-nums">{stats.pending}</div>
            </Card>
            <Card className="p-4 border-rose-200 bg-rose-50/40">
              <div className="flex items-center gap-2 text-xs text-slate-500"><XCircle size={13} /> فاشلة</div>
              <div className="text-3xl font-extrabold text-rose-700 tabular-nums">{stats.failed}</div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
            <Card className="p-4 border-slate-200">
              <h3 className="font-cairo font-bold text-slate-900 mb-2 text-sm">حالات الرسائل</h3>
              <div className="h-56" dir="ltr">
                {stats.total > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" innerRadius={40} outerRadius={75} paddingAngle={3}>
                        {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                      <Tooltip contentStyle={{ direction: "rtl", fontFamily: "Tajawal", fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <div className="h-full flex items-center justify-center text-slate-400 text-sm">لا توجد بيانات</div>}
              </div>
            </Card>
            <Card className="lg:col-span-2 p-4 border-slate-200">
              <h3 className="font-cairo font-bold text-slate-900 mb-2 text-sm">الرسائل حسب المرحلة</h3>
              <div className="h-56" dir="ltr">
                {stats.by_stage?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.by_stage} margin={{ top: 6, right: 6, left: 0, bottom: 6 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#475569" }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#475569" }} width={30} />
                      <Tooltip contentStyle={{ direction: "rtl", fontFamily: "Tajawal", fontSize: 12 }} />
                      <Bar dataKey="count" fill="#0F766E" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="h-full flex items-center justify-center text-slate-400 text-sm">لا توجد بيانات</div>}
              </div>
            </Card>
          </div>
        </>
      )}

      {/* Filters */}
      <Card className="p-4 sm:p-5 border-slate-200 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label>بحث (اسم/ولي/جوال)</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} className="h-10" dir="rtl" data-testid="wa-search" />
          </div>
          <div className="space-y-1.5">
            <Label>المرحلة</Label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger className="h-10" dir="rtl"><SelectValue /></SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">جميع المراحل</SelectItem>
                {STAGE_KEYS.map(k => <SelectItem key={k} value={k}>{STAGES[k]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>الصف</Label>
            <Select value={grade} onValueChange={setGrade}>
              <SelectTrigger className="h-10" dir="rtl"><SelectValue /></SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">جميع الصفوف</SelectItem>
                {GRADES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>الحالة</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-10" dir="rtl" data-testid="wa-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">الكل</SelectItem>
                <SelectItem value="sent">{STATUS_LABELS.sent}</SelectItem>
                <SelectItem value="pending">{STATUS_LABELS.pending}</SelectItem>
                <SelectItem value="failed">{STATUS_LABELS.failed}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>من تاريخ الغياب</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10" />
          </div>
          <div className="space-y-1.5">
            <Label>إلى تاريخ الغياب</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button onClick={handleExcel} variant="outline" className="h-10 gap-2 text-emerald-700 border-emerald-300" data-testid="btn-wa-excel"><FileSpreadsheet size={15} /> Excel</Button>
          <Button onClick={handlePrint} variant="outline" className="h-10 gap-2 text-teal-700 border-teal-300" data-testid="btn-wa-print"><Printer size={15} /> طباعة</Button>
          <Button onClick={load} disabled={loading} className="h-10 px-5 bg-teal-700 hover:bg-teal-800 text-white gap-2 btn-elevate" data-testid="btn-wa-search">
            {loading ? <Loader2 className="animate-spin" size={15} /> : <Search size={15} />}
            بحث
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm min-w-[820px]" data-testid="wa-table">
            <thead>
              <tr className="bg-teal-700 text-white">
                <th className="px-2 sm:px-3 py-2.5 text-center font-cairo w-12">#</th>
                <th className="px-2 sm:px-3 py-2.5 text-right font-cairo">الطالب</th>
                <th className="px-2 sm:px-3 py-2.5 text-right font-cairo">ولي الأمر</th>
                <th className="px-2 sm:px-3 py-2.5 text-right font-cairo">الجوال</th>
                <th className="px-2 sm:px-3 py-2.5 text-right font-cairo">المرحلة/الصف/الفصل</th>
                <th className="px-2 sm:px-3 py-2.5 text-right font-cairo">تاريخ الغياب</th>
                <th className="px-2 sm:px-3 py-2.5 text-center font-cairo">الحالة</th>
                <th className="px-2 sm:px-3 py-2.5 text-center font-cairo w-32">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-400"><Loader2 className="animate-spin inline mx-1" size={16} /> جاري التحميل...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-400">لا توجد رسائل مطابقة</td></tr>
              ) : rows.map((r, i) => (
                <tr key={r.id} className="odd:bg-white even:bg-slate-50/60 hover:bg-teal-50/40">
                  <td className="px-2 sm:px-3 py-2 text-center text-slate-500 tabular-nums">{i + 1}</td>
                  <td className="px-2 sm:px-3 py-2 text-slate-800">{r.student_name}</td>
                  <td className="px-2 sm:px-3 py-2 text-slate-700">{r.guardian_name || "—"}</td>
                  <td className="px-2 sm:px-3 py-2 text-slate-700 tabular-nums whitespace-nowrap" dir="ltr">{r.guardian_phone}</td>
                  <td className="px-2 sm:px-3 py-2 text-slate-700 text-xs"><div>{STAGES[r.stage]}</div><div className="text-slate-500">{r.grade} / {r.section}</div></td>
                  <td className="px-2 sm:px-3 py-2 text-slate-700 tabular-nums" dir="ltr">{r.absence_date}</td>
                  <td className="px-2 sm:px-3 py-2 text-center"><span className={`inline-block px-2 py-1 rounded text-[10px] font-cairo font-bold ${STATUS_STYLE[r.status]}`}>{STATUS_LABELS[r.status]}</span></td>
                  <td className="px-2 sm:px-3 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setViewing(r)} className="h-8 w-8 p-0 text-slate-600 hover:bg-slate-100" title="عرض الرسالة" data-testid={`wa-view-${r.id}`}>
                        <MessageCircle size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onResend(r)} className="h-8 w-8 p-0 text-teal-700 hover:bg-teal-100" title="إعادة إرسال" data-testid={`wa-resend-${r.id}`}>
                        <RefreshCw size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleting(r)} className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-100" title="حذف" data-testid={`wa-delete-${r.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500 bg-slate-50/60">
          إجمالي: <span className="font-bold text-slate-700 tabular-nums">{rows.length}</span>
        </div>
      </Card>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader><DialogTitle className="font-cairo">نص الرسالة المرسلة</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500">إلى: <span className="font-bold text-slate-800">{viewing.guardian_name || "—"}</span> · <span dir="ltr" className="tabular-nums">{viewing.guardian_phone}</span></div>
              <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-4 whitespace-pre-line text-sm text-slate-800 leading-relaxed">{viewing.message}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-cairo">حذف الرسالة</AlertDialogTitle>
            <AlertDialogDescription>هل تريد حذف هذه الرسالة من السجل؟</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700">حذف</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
